import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Button, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import IstoricDosareConexe from './dosar/IstoricDosareConexe';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { callAddFont } from './fonts/DejaVuSans-normal';
import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, WidthType, HeadingLevel, BorderStyle, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';

const ModalIstoricDosareConexe = ({ open, onClose, idDosar, numarDosar }) => {
    return (
      <Dialog 
        open={open} 
        onClose={onClose} 
        fullWidth 
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: '14px',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
          }
        }}
      >
        <DialogTitle className='titluModal'>
          Istoric dosare conexe {numarDosar ? `— ${numarDosar}` : ''}
          <IconButton
            aria-label="close"
            onClick={onClose}
            style={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#FFFFFF',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 3, px: 3, bgcolor: '#ffffff' }}>
          <IstoricDosareConexe idProp={idDosar} />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: '#fafafa', borderTop: '1px solid #e8e8ed' }}>
          <Box sx={{ display: 'flex', gap: 1, mr: 'auto' }}>
            <Button 
              onClick={() => exportPDF(idDosar, numarDosar)}
              startIcon={<PictureAsPdfIcon />}
              sx={{ 
                color: '#1d1d1f',
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '0.875rem',
                borderRadius: '8px',
                px: 2.5,
                border: '1px solid #e8e8ed',
                '&:hover': {
                  bgcolor: '#f5f5f7',
                  borderColor: '#d2d2d7',
                },
              }}
            >
              Export PDF
            </Button>
            <Button 
              onClick={() => exportDOCX(idDosar, numarDosar)}
              startIcon={<DescriptionIcon />}
              sx={{ 
                color: '#1d1d1f',
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '0.875rem',
                borderRadius: '8px',
                px: 2.5,
                border: '1px solid #e8e8ed',
                '&:hover': {
                  bgcolor: '#f5f5f7',
                  borderColor: '#d2d2d7',
                },
              }}
            >
              Export DOCX
            </Button>
          </Box>
          <Button 
            onClick={onClose} 
            sx={{ 
              color: '#6e6e73',
              fontWeight: 500,
              textTransform: 'none',
              fontSize: '0.875rem',
              borderRadius: '8px',
              px: 2.5,
              '&:hover': {
                bgcolor: '#f5f5f7',
                color: '#1d1d1f',
              },
            }}
          >
            Închidere
          </Button>
        </DialogActions>
      </Dialog>
    );
};

function getToken() {
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };
  return sessionStorage.getItem('token') || localStorage.getItem('token') || getCookie('_tokenCjpc');
}

async function fetchDosareConexe(idDosar) {
  const token = getToken();
  if (!token) {
    console.error('Nu s-a găsit token-ul de autentificare');
    return [];
  }
  try {
    const response = await fetch(`${window.API_URL}legaturiDosare/${idDosar}/detaliat`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });
    const result = await response.json();
    if (result && result.data) {
      return result.data;
    }
  } catch (err) {
    console.error('Eroare fetch dosare conexe:', err);
  }
  return [];
}

function formatDatePDF(dateStr) {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  } catch {
    return dateStr;
  }
}

function formatDateTimePDF(dateStr) {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  } catch {
    return dateStr;
  }
}

async function exportPDF(idDosar, numarDosar) {
  const dosareConexe = await fetchDosareConexe(idDosar);
  if (!dosareConexe.length) return;

  const doc = new jsPDF('p', 'mm', 'a4');
  callAddFont(doc);
  doc.setFont('DejaVuSans');

  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 15;

  // Title
  doc.setFontSize(14);
  doc.setTextColor(29, 29, 31);
  doc.text(`Istoric dosare conexe${numarDosar ? ' \u2014 ' + numarDosar : ''}`, 14, y);
  y += 8;

  doc.setFontSize(9);
  doc.setTextColor(110, 110, 115);
  doc.text(`Total dosare conexe: ${dosareConexe.length}`, 14, y);
  y += 4;

  doc.setDrawColor(232, 232, 237);
  doc.line(14, y, pageWidth - 14, y);
  y += 6;

  dosareConexe.forEach((dosar, idx) => {
    if (y > 260) {
      doc.addPage();
      y = 15;
    }

    // Dosar header
    doc.setFontSize(10);
    doc.setTextColor(29, 29, 31);
    doc.text(`${idx + 1}. ${dosar.numarDosarUnic}`, 14, y);
    
    doc.setFontSize(8);
    doc.setTextColor(110, 110, 115);
    const materieText = `${dosar.materie} | Creat: ${formatDatePDF(dosar.dataCreare)}`;
    doc.text(materieText, pageWidth - 14 - doc.getTextWidth(materieText), y);
    y += 5;

    // Obiect
    if (dosar.obiect) {
      doc.setFontSize(8);
      doc.setTextColor(66, 66, 69);
      const obiectLines = doc.splitTextToSize(`Obiect: ${dosar.obiect}`, pageWidth - 32);
      if (y + obiectLines.length * 3.5 > 275) {
        doc.addPage();
        y = 15;
      }
      doc.text(obiectLines, 16, y);
      y += obiectLines.length * 3.5 + 2;
    }

    // Stadii & Termene
    if (dosar.stadii) {
      dosar.stadii.forEach((stadiu) => {
        if (y > 265) {
          doc.addPage();
          y = 15;
        }

        doc.setFontSize(9);
        doc.setTextColor(29, 29, 31);
        doc.text(`${stadiu.denumire} \u2014 ${formatDatePDF(stadiu.data)}`, 16, y);
        y += 4;

        // Parti
        if (stadiu.parti && stadiu.parti.length > 0) {
          doc.setFontSize(7);
          doc.setTextColor(134, 134, 139);
          const partiText = stadiu.parti.map(p => `${p.numePrenume} (${p.calitate})`).join(', ');
          const partiLines = doc.splitTextToSize(`P\u0103r\u021bi: ${partiText}`, pageWidth - 36);
          if (y + partiLines.length * 3 > 275) {
            doc.addPage();
            y = 15;
          }
          doc.text(partiLines, 18, y);
          y += partiLines.length * 3 + 2;
        }

        // Termene as table
        if (stadiu.termene && stadiu.termene.length > 0) {
          const tableData = stadiu.termene.map(t => [
            formatDateTimePDF(t.dataTermen),
            t.tipSolutie || '-',
            (t.sumarSolutie || '-').substring(0, 200) + ((t.sumarSolutie || '').length > 200 ? '...' : ''),
          ]);

          if (y > 250) {
            doc.addPage();
            y = 15;
          }

          doc.autoTable({
            startY: y,
            margin: { left: 18, right: 14 },
            head: [['Data termen', 'Tip solu\u021bie', 'Sumar solu\u021bie']],
            body: tableData,
            styles: {
              font: 'DejaVuSans',
              fontSize: 7,
              cellPadding: 2,
              textColor: [66, 66, 69],
              lineColor: [232, 232, 237],
              lineWidth: 0.2,
            },
            headStyles: {
              fillColor: [245, 245, 247],
              textColor: [29, 29, 31],
              fontStyle: 'bold',
              fontSize: 7,
            },
            columnStyles: {
              0: { cellWidth: 30 },
              1: { cellWidth: 30 },
              2: { cellWidth: 'auto' },
            },
          });

          y = doc.lastAutoTable.finalY + 4;
        }
      });
    }

    // Separator
    if (y > 270) {
      doc.addPage();
      y = 15;
    }
    doc.setDrawColor(232, 232, 237);
    doc.line(14, y, pageWidth - 14, y);
    y += 6;
  });

  const fileName = `Istoric_dosare_conexe${numarDosar ? '_' + numarDosar.replace(/\//g, '-') : ''}.pdf`;
  doc.save(fileName);
}

async function exportDOCX(idDosar, numarDosar) {
  const dosareConexe = await fetchDosareConexe(idDosar);
  if (!dosareConexe.length) return;

  const children = [];

  // Title
  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `Istoric dosare conexe${numarDosar ? ' \u2014 ' + numarDosar : ''}`,
          bold: true,
          size: 28,
          color: '1d1d1f',
        }),
      ],
      heading: HeadingLevel.HEADING_1,
      spacing: { after: 200 },
    })
  );

  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `Total dosare conexe: ${dosareConexe.length}`,
          size: 18,
          color: '6e6e73',
        }),
      ],
      spacing: { after: 300 },
    })
  );

  dosareConexe.forEach((dosar, idx) => {
    // Dosar header
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `${idx + 1}. ${dosar.numarDosarUnic}`,
            bold: true,
            size: 22,
            color: '1d1d1f',
          }),
          new TextRun({
            text: `   ${dosar.materie} | Creat: ${formatDatePDF(dosar.dataCreare)}`,
            size: 18,
            color: '86868b',
          }),
        ],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 300, after: 100 },
      })
    );

    // Obiect
    if (dosar.obiect) {
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: 'Obiect: ', bold: true, size: 18, color: '1d1d1f' }),
            new TextRun({ text: dosar.obiect, size: 18, color: '424245' }),
          ],
          spacing: { after: 100 },
        })
      );
    }

    // User
    if (dosar.user) {
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: `Utilizator: ${dosar.user.fullname}`, size: 16, color: '6e6e73' }),
          ],
          spacing: { after: 200 },
        })
      );
    }

    // Stadii
    if (dosar.stadii) {
      dosar.stadii.forEach((stadiu) => {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `${stadiu.denumire} \u2014 ${formatDatePDF(stadiu.data)}`,
                bold: true,
                size: 20,
                color: '1d1d1f',
              }),
            ],
            spacing: { before: 200, after: 100 },
          })
        );

        // Parti
        if (stadiu.parti && stadiu.parti.length > 0) {
          children.push(
            new Paragraph({
              children: [
                new TextRun({ text: 'P\u0103r\u021bi: ', bold: true, size: 16, color: '86868b' }),
                new TextRun({
                  text: stadiu.parti.map(p => `${p.numePrenume} (${p.calitate})`).join(', '),
                  size: 16,
                  color: '424245',
                }),
              ],
              spacing: { after: 100 },
            })
          );
        }

        // Termene as table
        if (stadiu.termene && stadiu.termene.length > 0) {
          const noBorder = { style: BorderStyle.NONE, size: 0 };
          const thinBorder = { style: BorderStyle.SINGLE, size: 1, color: 'e8e8ed' };

          const headerRow = new TableRow({
            children: [
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: 'Data termen', bold: true, size: 16 })] })],
                width: { size: 20, type: WidthType.PERCENTAGE },
                borders: { top: thinBorder, bottom: thinBorder, left: noBorder, right: noBorder },
              }),
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: 'Tip solu\u021bie', bold: true, size: 16 })] })],
                width: { size: 20, type: WidthType.PERCENTAGE },
                borders: { top: thinBorder, bottom: thinBorder, left: noBorder, right: noBorder },
              }),
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: 'Sumar solu\u021bie', bold: true, size: 16 })] })],
                width: { size: 60, type: WidthType.PERCENTAGE },
                borders: { top: thinBorder, bottom: thinBorder, left: noBorder, right: noBorder },
              }),
            ],
          });

          const dataRows = stadiu.termene.map(t => 
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph({ children: [new TextRun({ text: formatDateTimePDF(t.dataTermen), size: 15 })] })],
                  borders: { top: noBorder, bottom: thinBorder, left: noBorder, right: noBorder },
                }),
                new TableCell({
                  children: [new Paragraph({ children: [new TextRun({ text: t.tipSolutie || '-', size: 15 })] })],
                  borders: { top: noBorder, bottom: thinBorder, left: noBorder, right: noBorder },
                }),
                new TableCell({
                  children: [new Paragraph({ children: [new TextRun({ text: (t.sumarSolutie || '-').substring(0, 500), size: 15 })] })],
                  borders: { top: noBorder, bottom: thinBorder, left: noBorder, right: noBorder },
                }),
              ],
            })
          );

          children.push(
            new Table({
              rows: [headerRow, ...dataRows],
              width: { size: 100, type: WidthType.PERCENTAGE },
            })
          );

          children.push(new Paragraph({ text: '', spacing: { after: 200 } }));
        }
      });
    }

    // Separator
    children.push(
      new Paragraph({
        children: [new TextRun({ text: '\u2500'.repeat(80), color: 'e8e8ed', size: 16 })],
        spacing: { before: 200, after: 200 },
      })
    );
  });

  const doc = new Document({
    sections: [{
      children: children,
    }],
  });

  const blob = await Packer.toBlob(doc);
  const fileName = `Istoric_dosare_conexe${numarDosar ? '_' + numarDosar.replace(/\//g, '-') : ''}.docx`;
  saveAs(blob, fileName);
}

export default ModalIstoricDosareConexe;
